import * as React from "react";
import ReactDOM from "react-dom";
import { Table, Pagination } from "antd";
import { TableEventListeners, TableProps } from "antd/lib/table";

import "antd/lib/table/style";
import "antd/lib/pagination/style";
import "./VhTable.scss";

/**
 * 分页属性
 *
 * @export
 * @interface VhPagination
 */
export interface VhPagination {
  /**
   * 每页大小
   *
   * @type {number}
   * @memberof VhPagination
   */
  pageSize: number;
  /**
   * 当前页码
   *
   * @type {number}
   * @memberof VhPagination
   */
  current: number;
  /**
   * 总数据条数
   *
   * @type {number}
   * @memberof VhPagination
   */
  total?: number;
  /**
   * 总页数
   *
   * @type {number}
   * @memberof VhPagination
   */
  totalPages?: number;
}

/**
 * 列表属性
 *
 * @export
 * @interface VhTableProps
 * @template T
 */
export interface VhTableProps<T> extends TableProps<T> {
  /**
   * 是否展示外边框和列边框
   *
   * @type {boolean}
   * @memberof VhTableProps
   */
  bordered?: boolean;
  /**
   * 表格列配置
   *
   * @type {any[]}
   * @memberof VhTableProps
   */
  columns: any[];
  /**
   * 表格数据源
   *
   * @type {any[]}
   * @memberof VhTableProps
   */
  dataSource: any[];
  /**
   * 加载状态
   *
   * @type {boolean}
   * @memberof VhTableProps
   */
  loading?: boolean;
  /**
   * 空数据自定义
   *
   * @type {{}}
   * @memberof VhTableProps
   */
  locale?: {};
  /**
   * 自定义table高度所减去的高度
   *
   * @type {number}
   * @memberof VhTableProps
   */
  otherHeight?: number;
  /**
   * 分页
   *
   * @type {*}
   * @memberof VhTableProps
   */
  pagination?: any;
  /**
   * RowKey
   *
   * @memberof VhTableProps
   */
  rowKey?: string | ((record: T, index: number) => string);
  /**
   * 行选择
   *
   * @type {*}
   * @memberof VhTableProps
   */
  rowSelection?: any;
  /**
   * x，y scrollbar
   *
   * @type {({
   *     x?: boolean | number | string;
   *     y?: boolean | number | string;
   *   })}
   * @memberof VhTableProps
   */
  scroll?: {
    x?: boolean | number | string;
    y?: boolean | number | string;
  };
  /**
   * 头部是否显示
   *
   * @type {boolean}
   * @memberof VhTableProps
   */
  showHeader?: boolean;
  /**
   * 表格大小
   */
  size?: "default" | "middle" | "small";
  /**
   * 分页（current，pageSize）变动回调
   *
   * @memberof VhTableProps
   */
  onChange?: (pagination: VhPagination) => void;
  /**
   * 设置行属性
   *
   * @memberof VhTableProps
   */
  onRow?: (record: T, index: number) => TableEventListeners;
}

interface VhTableState {
  height: number
}

/**
 * 通用table，动态设置高度
 *
 * @class VhTable
 * @extends {React.Component<VhTableProps, VhTableState>}
 */
class VhTable extends React.Component<VhTableProps<any>, VhTableState> {
  /**
   * Creates an instance of VhTable.
   * @param {VhTableProps<any>} props
   * @memberof VhTable
   */
  constructor(props: VhTableProps<any>) {
    super(props)

    // 初始化state
    this.state = {
      height: 300
    };
  }

  /**
   * 组件加载后
   *
   * @memberof VhTable
   */
  public componentDidMount() {
    // 给window添加浏览器窗口调整大小事件
    window.addEventListener("resize", this.handleResize);
    this.handleResize();
  }

  /**
   * 组件获取到新的数据源。重新设置table高度
   *
   * @param {VhTableProps} props
   * @param {VhTableState} state
   * @memberof VhTable
   */
  public UNSAFE_componentWillReceiveProps(newProps: VhTableProps<any>) {
    if (this.props.dataSource !== newProps.dataSource) {
      this.setTableOverflowY();
    }
  }

  /**
   * 组件更新后。重新设置table高度
   *
   * @param {MsTableProps} props
   * @param {MsTableState} state
   * @memberof MsTable
   */
  public componentDidUpdate(prevProps: VhTableProps<any>) {
    if (this.props.dataSource !== prevProps.dataSource) {
      this.setTableHeight(this.state.height);
    }
  }

  /**
   * 组件卸载前
   *
   * @memberof VhTable
   */
  public componentWillUnmount() {
    // 删除window窗口调整事件
    window.removeEventListener("resize", this.handleResize);
  }

  /**
   * 切换页码或是切换pageSize
   *
   * @memberof VhTable
   */
  public onChange = (type: number, page: number, pageSize: number) => {
    if (this.props.onChange) {
      const pagination: VhPagination = {current: page, pageSize};
      this.props.onChange(pagination);

      if (type === 1) {
        const table: any = ReactDOM.findDOMNode(this);
        const tableBody = table.querySelector(".ant-table-body");
        tableBody.scrollTop = 0;
      }
    }
  }

  // 设置窗口调整回调函数
  public handleResize = () => {
    const h = this.setTableOverflowY();
    this.setTableHeight(h);
  };

  public render() {
    let className = "vh-table-default";

    if (this.props.rowSelection) {
      className = "vh-table-default vh-table-default-row-select";
    }

    return (
      <div className="vh-table-container-default">
        <Table
          { ...this.props }
          className={ className }
          size="middle"
          scroll={ {y: this.state.height, ...this.props.scroll} }
          pagination={ false }
        />
        <div style={ {textAlign: "right"} }>
        {
          this.props.pagination && this.props.dataSource && this.props.dataSource.length ?
          <Pagination
            onChange={ this.onChange.bind(this, 1) }
            onShowSizeChange={ this.onChange.bind(this, 2) }
            {
              ...{
                pageSizeOptions: ["25", "50", "100"],
                showTotal: (total: number) => `共${ total }条`,
                showSizeChanger: true,
                showQuickJumper: true,
                style: {padding: "8px 0"},
                ...this.props.pagination
              }
            }
          /> : null
        }
        </div>
      </div>
    )
  }

  private setTableOverflowY = (): number => {
    // table对象
    const table: any = ReactDOM.findDOMNode(this);
    // root组件
    const root = document.querySelector("#root") || document.body;
    const header = root.querySelector(".ms-header") || root.querySelector(".vh-header");
    const headerHeight = header ? header.clientHeight : 0;
    // layout高度 - 头部64 - 页底padding 8 - 调整用height
    const totalHeight: number = root.clientHeight - headerHeight - 8 - (this.props.otherHeight || 0);
    // 减去表头和分页
    const antTableThead: any = table.querySelector(".ant-table-header");
    // Theader高46  分页48
    let h = totalHeight - (antTableThead ? antTableThead.clientHeight : 46) - 48;
    // tabs组件初始化
    let tabs = null;

    // 判断table上两层是否存在tabs组件
    if (table.parentNode.className.includes("ant-tabs-tabpane")) {
      tabs = table.parentNode.parentNode.parentNode;
    } else if (table.parentNode.parentNode.className.includes("ant-tabs-tabpane")) {
      tabs = table.parentNode.parentNode.parentNode.parentNode;
    }

    // 存在tabs的场合
    if (tabs) {
      // 减去tablist高度
      h -= 60;

      // 减去tabs同级的各个组件
      for (const item of tabs.parentNode.childNodes) {
        if (item !== tabs && (item.style && !item.style.float)) {
          const style = item.currentStyle || window.getComputedStyle(item);
          const top = style.marginTop === "auto" ? "0px" : style.marginTop;
          const bottom = style.marginBottom === "auto" ? "0px" : style.marginBottom;
          h -= item.offsetHeight;

          // 减去marginTop
          if (top) {
            h -= Number(top.replace("px", ""));
          }

          // 减去marginBottom
          if (bottom) {
            h -= Number(bottom.replace("px", ""));
          }
        }
      }
    }

    // 减去搜索框等
    for (const item of table.parentNode.childNodes) {
      if (item !== table && (item.style && !item.style.float)) {
        const style = item.currentStyle || window.getComputedStyle(item);
        const top = style.marginTop === "auto" ? "0px" : style.marginTop;
        const bottom = style.marginBottom === "auto" ? "0px" : style.marginBottom;
        h -= item.offsetHeight;

        // 减去marginTop
        if (top) {
          h -= Number(top.replace("px", ""));
        }

        // 减去marginBottom
        if (bottom) {
          h -= Number(bottom.replace("px", ""));
        }
      }
    }

    // 设置table高度
    this.setState({height: h});
    return h;
  }

  /**
   * 设置table高度
   *
   * @private
   * @memberof VhTable
   */
  private setTableHeight = (height: number) => {
    const table: any = ReactDOM.findDOMNode(this);
    const body = table.querySelector(".ant-table-body");
    const placeholder = table.querySelector(".ant-table-placeholder");
    const dataSource = this.props.dataSource;

    // 清空body高度
    if (body && body.style) {
      body.style.height = "";
    }

    // 清空placeholder高度
    if (placeholder && placeholder.style) {
      placeholder.style.height = "";
    }

    // 数据存在的场合
    if (dataSource && dataSource.length) {
      // body存在的场合
      if (body) {
        // 设置body的高度
        body.style.height = height + "px";
      }
    } else {
      // 数据不存在的场合
      if (placeholder) {
        // 设置placeholder高度并居中（分页要加上）
        placeholder.style.height = height + 48 + "px";
      }
    }
  }
}

export default VhTable;
